import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { SPORTS, US_STATES, FOCUS_AREAS } from '../utils/constants';
import ImageUpload from '../components/ImageUpload';
import './ProfilePage.css';

const SPORT_OPTIONS = [
  { label: '🏀 Basketball',       value: 'Basketball'        },
  { label: '🏈 Football',         value: 'Football'          },
  { label: '⚽ Soccer',           value: 'Soccer'            },
  { label: '⚾ Baseball/Softball', value: 'Baseball/Softball' },
  { label: '🎾 Tennis',           value: 'Tennis'            },
  { label: '🏐 Volleyball',       value: 'Volleyball'        },
  { label: '🤼 Wrestling',        value: 'Wrestling'         },
  { label: '⛳ Golf',             value: 'Golf'              },
];

function TagPicker({ options, selected, onChange, max }) {
  const toggle = (value) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      if (max && selected.length >= max) return;
      onChange([...selected, value]);
    }
  };
  return (
    <div className="tag-picker">
      {options.map((opt) => {
        const value = typeof opt === 'string' ? opt : opt.value;
        const label = typeof opt === 'string' ? opt : opt.label;
        const isSelected = selected.includes(value);
        return (
          <button
            key={value}
            type="button"
            className={`tag-option${isSelected ? ' selected' : ''}`}
            onClick={() => toggle(value)}
          >
            {isSelected && <span className="tag-check">✓</span>}
            {label}
          </button>
        );
      })}
    </div>
  );
}

function ProfilePage() {
  const { profile, updateProfile } = useAuth();
  const [formData, setFormData] = useState({ full_name: '', sport: '', secondary_sport: '', city: '', state: '', bio: '', hourly_rate: '', grad_year: '' });
  const [focusAreas, setFocusAreas] = useState([]);
  const [sportsInterest, setSportsInterest] = useState([]);
  const [focusAreasInterest, setFocusAreasInterest] = useState([]);
  const [avatarUrl, setAvatarUrl] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (profile) {
      setFormData({
        full_name: [profile.first_name, profile.last_name].filter(Boolean).join(' '),
        sport: profile.sport || '', secondary_sport: profile.secondary_sport || profile.sport_2 || '',
        city: profile.city || '', state: profile.state || '',
        bio: profile.bio || '', hourly_rate: profile.hourly_rate != null ? String(profile.hourly_rate) : '',
        grad_year: profile.grad_year != null ? String(profile.grad_year) : '',
      });
      setAvatarUrl(profile.avatar_url || '');
      setSportsInterest(profile.sports_interest || []);
      setFocusAreasInterest(profile.focus_areas_interest || []);
      const savedFocusAreas = profile.focus_areas || profile.tags;
      if (Array.isArray(savedFocusAreas)) { setFocusAreas(savedFocusAreas.filter(Boolean)); } else if (typeof savedFocusAreas === 'string' && savedFocusAreas.trim()) { setFocusAreas(savedFocusAreas.split(',').map((t) => t.trim()).filter(Boolean)); } else { setFocusAreas([]); }
    }
  }, [profile]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setSuccess(false);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const toggleItem = (setter, current, value) => {
    setter(current.includes(value) ? current.filter((v) => v !== value) : [...current, value]);
    setSuccess(false);
  };

  const validate = () => {
    const errs = {};
    if (!formData.full_name.trim()) errs.full_name = 'Full name is required.';
    if (profile?.role === 'mentor') {
      if (!formData.sport) errs.sport = 'Sport is required.';
      if (formData.secondary_sport && formData.secondary_sport === formData.sport) errs.secondary_sport = 'Secondary sport must be different from your primary sport.';
      if (!formData.city.trim()) errs.city = 'City is required.';
      if (!formData.state) errs.state = 'State is required.';
      if (formData.hourly_rate && isNaN(parseFloat(formData.hourly_rate))) errs.hourly_rate = 'Enter a valid number.';
    }
    return errs;
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError(''); setSuccess(false);
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    try {
      const nameTrimmed = formData.full_name.trim();
      const nameSpaceIdx = nameTrimmed.indexOf(' ');
      const updates = {
        first_name: nameSpaceIdx === -1 ? nameTrimmed : nameTrimmed.slice(0, nameSpaceIdx),
        last_name: nameSpaceIdx === -1 ? '' : nameTrimmed.slice(nameSpaceIdx + 1).trim(),
        sport: formData.sport || null, secondary_sport: formData.secondary_sport || null,
        grad_year: formData.grad_year ? parseInt(formData.grad_year) : null,
        focus_areas: focusAreas.length ? focusAreas : null,
        sports_interest: sportsInterest.length ? sportsInterest : null,
        focus_areas_interest: focusAreasInterest.length ? focusAreasInterest : null,
        city: formData.city.trim() || null, state: formData.state || null,
        bio: formData.bio.trim() || null,
        hourly_rate: formData.hourly_rate ? parseFloat(formData.hourly_rate) : null,
        avatar_url: avatarUrl || null,
        tags: focusAreas.length > 0 ? focusAreas : null,
      };
      await updateProfile(updates);
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (url) => {
    setAvatarUrl(url);
    setSuccess(false);
    try {
      await updateProfile({ avatar_url: url });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Avatar save error:', err.message);
      }
    }
  };
  const isMentor = profile?.role === 'mentor';

  return (
    <div className="profile-page">
      <div className="profile-page-inner">

        <div className="profile-page-header">
          <h1 className="profile-title">My Profile</h1>
          <p className="profile-role-badge">
            {isMentor ? '⭐ Trainer' : '👨‍👩‍👧 Parent'}
          </p>
        </div>

        <div className={`profile-layout${!isMentor ? ' profile-layout-single' : ''}`}>

          {/* ── Main form card ── */}
          <div className="profile-card">

            {success && <div className="alert alert-success">Profile saved successfully!</div>}
            {error && <div className="alert alert-error">{error}</div>}

            {/* Photo */}
            <div className="profile-photo-section">
              <ImageUpload
                currentUrl={avatarUrl}
                onUploadComplete={handleAvatarUpload}
                initials={
                  [profile?.first_name?.[0], profile?.last_name?.[0]]
                    .filter(Boolean)
                    .join('')
                    .toUpperCase() || '?'
                }
              />
            </div>

            <form onSubmit={handleSave} noValidate>

              {/* Full name */}
              <div className="form-group">
                <label className="form-label" htmlFor="full_name">Full Name</label>
                <input
                  id="full_name"
                  name="full_name"
                  type="text"
                  className={`form-input${errors.full_name ? ' error' : ''}`}
                  value={formData.full_name}
                  onChange={handleChange}
                  autoComplete="name"
                />
                {errors.full_name && <span className="form-error">{errors.full_name}</span>}
              </div>

              {/* ── MENTOR FIELDS ── */}
              {isMentor && (
                <>
                  <div className="profile-section-divider" />
                  <span className="profile-section-label">Sport &amp; Rate</span>

                  {/* Primary + Secondary sport */}
                  <div className="form-row form-row-top">
                    <div className="form-group">
                      <label className="form-label" htmlFor="sport">Primary Sport</label>
                      <select
                        id="sport"
                        name="sport"
                        className={`form-select${errors.sport ? ' error' : ''}`}
                        value={formData.sport}
                        onChange={handleChange}
                      >
                        <option value="">Select sport</option>
                        {SPORTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      {errors.sport && <span className="form-error">{errors.sport}</span>}
                    </div>

                    <div className="form-group">
                      <label className="form-label" htmlFor="secondary_sport">
                        Additional Sport (Optional)
                      </label>
                      <select
                        id="secondary_sport"
                        name="secondary_sport"
                        className="form-select"
                        value={formData.secondary_sport}
                        onChange={handleChange}
                      >
                        <option value="">None</option>
                        {SPORTS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                      <span className="form-hint">Add a second sport you can coach</span>
                    </div>
                  </div>

                  {/* Hourly rate */}
                  <div className="form-group form-group-half">
                    <label className="form-label" htmlFor="hourly_rate">Hourly Rate ($)</label>
                    <input
                      id="hourly_rate"
                      name="hourly_rate"
                      type="number"
                      min="0"
                      step="0.01"
                      className={`form-input${errors.hourly_rate ? ' error' : ''}`}
                      value={formData.hourly_rate}
                      onChange={handleChange}
                      placeholder="e.g. 25"
                    />
                    <span className="form-hint">Collected in person from the parent.</span>
                    {errors.hourly_rate && <span className="form-error">{errors.hourly_rate}</span>}
                  </div>

                  {/* Grad Year */}
                  <div className="form-group form-group-half">
                    <label className="form-label" htmlFor="grad_year">Graduation Year</label>
                    <input
                      id="grad_year"
                      name="grad_year"
                      type="number"
                      min={2025}
                      max={2032}
                      className="form-input"
                      value={formData.grad_year}
                      onChange={handleChange}
                      placeholder="e.g. 2026"
                    />
                  </div>

                  <div className="profile-section-divider" />
                  <span className="profile-section-label">Focus Areas</span>

                  {/* Focus area tag picker */}
                  <div className="form-group">
                    <label className="form-label">What areas do you feel confident teaching?</label>
                    <p className="tag-picker-desc">
                      These appear as tags on your profile card — parents can see exactly what you specialise in.
                    </p>
                    <TagPicker
                      options={FOCUS_AREAS}
                      selected={focusAreas}
                      onChange={setFocusAreas}
                      max={5}
                    />
                    {focusAreas.length > 0 && (
                      <span className="tag-hint">{focusAreas.length} of 5 selected</span>
                    )}
                  </div>

                  <div className="profile-section-divider" />
                  <span className="profile-section-label">About You</span>

                  {/* Bio */}
                  <div className="form-group">
                    <label className="form-label" htmlFor="bio">Bio</label>
                    <textarea
                      id="bio"
                      name="bio"
                      className="form-textarea"
                      value={formData.bio}
                      onChange={handleChange}
                      placeholder="Tell parents about yourself and your sports experience…"
                      rows={4}
                    />
                  </div>
                </>
              )}

              {/* ── PARENT FIELDS ── */}
              {!isMentor && (
                <>
                  <div className="profile-section-divider" />
                  <span className="profile-section-label">Your Child's Interests</span>

                  <div className="form-group">
                    <label className="form-label">Sports Your Child Is Interested In</label>
                    <p className="tag-picker-desc">
                      Used to suggest the best trainers for your child.
                    </p>
                    <TagPicker
                      options={SPORT_OPTIONS}
                      selected={sportsInterest}
                      onChange={setSportsInterest}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Areas to Improve</label>
                    <p className="tag-picker-desc">
                      Helps us match you with trainers who specialise in these areas.
                    </p>
                    <TagPicker
                      options={FOCUS_AREAS}
                      selected={focusAreasInterest}
                      onChange={setFocusAreasInterest}
                    />
                  </div>
                </>
              )}

              {/* ── City + State (both roles) ── */}
              <div className="profile-section-divider" />
              <span className="profile-section-label">Location</span>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="city">
                    City{isMentor && <span style={{ color: '#f87171' }}> *</span>}
                  </label>
                  <input
                    id="city"
                    name="city"
                    type="text"
                    className={`form-input${errors.city ? ' error' : ''}`}
                    value={formData.city}
                    onChange={handleChange}
                    placeholder="Your city"
                  />
                  {errors.city && <span className="form-error">{errors.city}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="state">
                    State{isMentor && <span style={{ color: '#f87171' }}> *</span>}
                  </label>
                  <select
                    id="state"
                    name="state"
                    className={`form-select${errors.state ? ' error' : ''}`}
                    value={formData.state}
                    onChange={handleChange}
                  >
                    <option value="">Select state</option>
                    {US_STATES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  {errors.state && <span className="form-error">{errors.state}</span>}
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-full"
                disabled={saving}
                style={{ marginTop: '0.5rem' }}
              >
                {saving ? 'Saving…' : 'Save Profile'}
              </button>

            </form>
          </div>

          {/* ── Tips panel (mentor only) ── */}
          {isMentor && (
            <div className="profile-tips">
              <h3 className="profile-tips-title">Profile Tips</h3>
              <ul className="profile-tips-list">
                <li>Add a clear, friendly photo — profiles with photos get more requests</li>
                <li>Write a bio that highlights your experience and what you love about the sport</li>
                <li>Set a fair hourly rate — $15–$40/hr is common for teen trainers</li>
                <li>Keep your city and state up to date so parents nearby can find you</li>
                <li>Select the focus areas you're most confident in — this helps parents find you</li>
              </ul>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
